import React, { useRef, useState, useCallback } from 'react';
import { Box, useOutsideClick } from '@televet/kibble-ui/build/chakra';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { ChannelFilterSelectionFragment } from 'shared/types/graphql';
import { PopoverPlacement } from 'shared/components/Dropdown';
import ChannelListFilterPopoverContent from './ChannelListFilterPopoverContent';
import { IChannelListFilterSelection } from 'pages/Conversations/interfaces/IChannelListFilterSelection';
import { IChannelListFilter } from 'pages/Conversations/interfaces/IChannelListFilter';
import { IFilterPopoverState, FilterPopoverView } from '../interfaces/IFilterPopoverState';
import SaveChannelListFilterModal from './SaveChannelListFilterModal';
import DeleteChannelListFilterAlertDialog from './DeleteChannelListFilterAlertDialog';
import popperMaxSizeModifiers from 'shared/utils/popperMaxSizeModifiers';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { defaultChannelFilterSelectionDraft } from '../types/ChannelListFilterSelectionDraft';
import { IFilterPopoverEditState } from '../interfaces/IFilterPopoverEditState';
import { useCustomToast } from 'shared/hooks/useCustomToast';

interface IChannelListFilterPopoverProps {
  isOpen: boolean;
  trigger: JSX.Element;
  currentChannelFilterSelection: IChannelListFilterSelection;
  onSelectFilter: (channelFilterSelection: IChannelListFilterSelection) => void;
  onClearFilter: (source?: string, ignoreKeys?: (keyof IChannelListFilter)[]) => void;
  onClose: () => void;
  onShowArchivedToggle: () => void;
}

const ChannelListFilterPopover = ({
  isOpen,
  trigger,
  currentChannelFilterSelection,
  onSelectFilter,
  onClearFilter,
  onClose,
  onShowArchivedToggle,
}: IChannelListFilterPopoverProps): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const initialFocusRef = useRef<HTMLDivElement | null>(null);
  const [isSavingNewChannelListFilter, setIsSavingNewChannelListFilter] = useState(false);

  const [filterPopoverState, setFilterPopoverState] = usePersistedState<IFilterPopoverState>(
    PersistedStateKeys.ChannelListFilterPopoverState,
    {
      view: FilterPopoverView.Edit,
    },
  );

  const [filterPopoverEditState, setFilterPopoverEditState] = useState<IFilterPopoverEditState>({
    filterDraft: defaultChannelFilterSelectionDraft,
    channelFilterSelection: null,
  });

  const [filterToDelete, setFilterToDelete] = useState<ChannelFilterSelectionFragment | undefined>(undefined);

  const handleDeleteFilter = useCallback(
    async (channelFilterSelection: ChannelFilterSelectionFragment) => {
      setFilterToDelete(channelFilterSelection);
    },
    [setFilterToDelete],
  );

  const handleEditFilterComplete = useCallback(() => {
    setFilterPopoverState((value) => ({
      ...value,
      view: FilterPopoverView.Select,
    }));
  }, [setFilterPopoverState]);

  const handleSaveNewFilterSuccess = useCallback(
    (channelFilterSelection: ChannelFilterSelectionFragment): void => {
      toastMessagePop('New filter saved');
      onSelectFilter(channelFilterSelection);
      setIsSavingNewChannelListFilter(false);
    },
    [onSelectFilter, toastMessagePop],
  );

  const handleDeleteFilterSuccess = useCallback((): void => {
    toastMessagePop('Filter deleted');
    setFilterToDelete(undefined);
    if (filterToDelete?.id === currentChannelFilterSelection.id) {
      onClearFilter();
    }
    handleEditFilterComplete();
  }, [filterToDelete?.id, currentChannelFilterSelection.id, toastMessagePop, handleEditFilterComplete, onClearFilter]);

  const popoverContainerRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick({
    ref: popoverContainerRef,
    handler: () => {
      if (isOpen && !isSavingNewChannelListFilter && !filterToDelete?.id) {
        onClose();
      }
    },
  });

  return (
    <>
      <Popover
        anchor={trigger}
        isOpen={isOpen}
        closeOnBlur={false}
        isLazy={true}
        placement={PopoverPlacement.BottomStart}
        strategy="fixed"
        preventOverflow={true}
        boundary={document.body}
        eventListeners={{ resize: true }}
        modifiers={popperMaxSizeModifiers}
        onClose={onClose}
        variant="default"
        size="xl"
      >
        <Box ref={popoverContainerRef} m={-3}>
          <ChannelListFilterPopoverContent
            filterPopoverState={filterPopoverState}
            filterPopoverEditState={filterPopoverEditState}
            currentChannelFilterSelection={currentChannelFilterSelection}
            initialFocusRef={initialFocusRef}
            onSetFilterPopoverState={setFilterPopoverState}
            onSetFilterPopoverEditState={setFilterPopoverEditState}
            onSelectFilter={onSelectFilter}
            onEditFilterComplete={handleEditFilterComplete}
            onClearFilter={onClearFilter}
            onDeleteFilter={handleDeleteFilter}
            onSaveNewFilter={(): void => {
              setIsSavingNewChannelListFilter(true);
            }}
            onClosePopover={onClose}
            onShowArchivedToggle={onShowArchivedToggle}
          />
        </Box>
      </Popover>
      <SaveChannelListFilterModal
        isOpen={isSavingNewChannelListFilter}
        currentChannelFilterSelection={currentChannelFilterSelection}
        onSuccess={handleSaveNewFilterSuccess}
        onCancel={(): void => {
          setIsSavingNewChannelListFilter(false);
        }}
      />
      <DeleteChannelListFilterAlertDialog
        channelFilterSelection={filterToDelete} // TODO: Decouple this from filterPopoverState
        isOpen={!!filterToDelete?.id}
        onSuccess={handleDeleteFilterSuccess}
        onCancel={(): void => {
          setFilterToDelete(undefined);
        }}
      />
    </>
  );
};

export default ChannelListFilterPopover;
